// src/components/EntitlementGuard.tsx
import React, { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { Navigate } from "react-router-dom";
import { getAppContext } from "../utils/getAppContext";
import { getBrandConfig } from "../utils/brandConfig";

interface EntitlementGuardProps {
  children: React.ReactNode;
  requiredFeature?: string;
}

export function EntitlementGuard({
  children,
  requiredFeature,
}: EntitlementGuardProps) {
  const { isAuthenticated, entitlements, org } = useAuth();

  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      if (!isAuthenticated) {
        console.log(
          "[EntitlementGuard] Redirecting to /unauthorized — user is not authenticated",
        );
        setHasAccess(false);
        setLoading(false);
        return;
      }

      if (!requiredFeature) {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      console.log("[EntitlementGuard] Checking entitlements:", entitlements);

      if (!entitlements) {
        // Entitlements not yet loaded — stay in loading state, do not deny
        return;
      }

      const feature = entitlements?.features?.[requiredFeature];
      const isEnabled = feature?.enabled === true;

      console.log(`[EntitlementGuard] Feature "${requiredFeature}":`, feature);
      console.log(`[EntitlementGuard] isEnabled: ${isEnabled}`);

      if (!isEnabled) {
        console.log(
          `[EntitlementGuard] Redirecting to /unauthorized — "${requiredFeature}" is not enabled for this org`,
        );
      }

      setHasAccess(isEnabled);
      setLoading(false);
    };

    checkAccess();
  }, [isAuthenticated, requiredFeature, entitlements, org]);

  if (isAuthenticated === null) return null;
  if (isAuthenticated === false) {
    return <Navigate to={getBrandConfig(getAppContext()).loginPath} replace />;
  }

  if (loading || hasAccess === null) return null;

  if (!hasAccess) {
    console.log(
      `[EntitlementGuard] Redirecting to /unauthorized — access denied for "${requiredFeature}" (render phase)`,
    );
    return <Navigate to="/unauthorized" replace />;
  }

  // Access granted
  return <>{children}</>;
}
